import type { ServiceHealth } from './service-health';

/**
 * Monitoring alerts (Phase 07 Stage 02). An alert records an incident: a period in which a service
 * needed attention. Health itself is never decided here — it comes from `deriveServiceHealth`, so
 * an alert can only open where real, current check results show a problem.
 *
 *  - Opens when the service's health becomes `needs_attention`.
 *  - Resolves ONLY when the service is `healthy` again: every enabled check has a new, current
 *    passing result. That is a recovery.
 *  - **Unknown is not recovered.** While the service is `unknown` — a check was changed and its new
 *    result has not arrived, a result went stale, a check was disabled — an open alert stays open,
 *    pending a meaningful result. Unknown never opens an alert either.
 *  - One open alert per service at a time (also enforced by the database).
 */
export const ALERT_STATES = ['open', 'resolved'] as const;
export type AlertState = (typeof ALERT_STATES)[number];

/** Why the alert was opened: the health reason at that moment. */
export const ALERT_REASONS = ['check_failing', 'check_warning'] as const;
export type AlertReason = (typeof ALERT_REASONS)[number];

/** How an alert ends: only by recovery (the service is healthy again). */
export const ALERT_RESOLUTIONS = ['recovered'] as const;
export type AlertResolution = (typeof ALERT_RESOLUTIONS)[number];

export type AlertTransition =
  | { action: 'open'; reason: AlertReason }
  | { action: 'keep' }
  | { action: 'resolve'; resolution: AlertResolution }
  | { action: 'none' };

function isAlertReason(reason: string): reason is AlertReason {
  return (ALERT_REASONS as readonly string[]).includes(reason);
}

/** What should happen to a service's alert, given its current health. */
export function planAlertTransition(
  health: Pick<ServiceHealth, 'state' | 'reason'>,
  hasOpenAlert: boolean,
): AlertTransition {
  if (hasOpenAlert) {
    // Only a healthy service has recovered; needs attention or unknown keep the incident open.
    return health.state === 'healthy'
      ? { action: 'resolve', resolution: 'recovered' }
      : { action: 'keep' };
  }
  // deriveServiceHealth only gives needs_attention for a failing or warning check.
  return health.state === 'needs_attention' && isAlertReason(health.reason)
    ? { action: 'open', reason: health.reason }
    : { action: 'none' };
}
